import React from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Check, ArrowRight, ArrowLeft, ShieldCheck, Wallet, Radio } from "lucide-react";
import PJPCoin from "@/components/PJPCoin";
import { DIGITAL_ID_PRICE, DIGITAL_ID_LOOKUP_KEY } from "@/data/pjp";
import { api } from "@/lib/api";
import { redirectExternal } from "@/lib/redirect";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * DigitalIdDemo — public preview of the PJP Digital ID Badge.
 *
 * Anonymous visitors can click the "+ DIGITAL ID BADGE · $199" chip on any
 * pricing tier and land here. Shows a fully-populated mock badge (Jeff sample
 * data) + explains what unlocks and the wallet integration, with a single
 * primary CTA to add it to their membership.
 */
export default function DigitalIdDemo() {
  const { user } = useAuth();

  const buy = async () => {
    if (!user) {
      window.sessionStorage.setItem("pjp_pending_digital_id", "1");
      window.location.assign(`/register?next=/digital-id/demo&lookup=${encodeURIComponent(DIGITAL_ID_LOOKUP_KEY)}`);
      return;
    }
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: DIGITAL_ID_LOOKUP_KEY,
        origin_url: window.location.origin,
      });
      if (!redirectExternal(r.data.checkout_url)) {
        toast.error("Popup blocked. Please allow pop-ups and try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout unavailable");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian py-10 px-4" data-testid="page-digital-id-demo">
      <div className="max-w-5xl mx-auto">
        {/* Breadcrumb / back */}
        <Link to="/membership" className="mono text-[10px] tracking-widest text-cyan/80 hover:text-cyan flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3 w-3" /> BACK TO MEMBERSHIP
        </Link>

        <div className="grid md:grid-cols-[minmax(0,420px)_1fr] gap-10 items-start">
          {/* LEFT — the badge */}
          <MockBadge />

          {/* RIGHT — copy + CTA */}
          <div>
            <div className="mono text-[10px] tracking-widest text-orange-hud mb-2">// PJP · DIGITAL ID BADGE</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-4">
              Your aviation identity — <span className="text-cyan">verified.</span>
            </h1>
            <p className="text-white/70 leading-relaxed mb-6">
              A shareable, wallet-ready credential that any operator, FBO, or crew scheduler can scan
              in seconds. Backed by an FAA registry lookup and re-verified every scan.
            </p>

            <ul className="space-y-3 mb-8">
              {[
                { icon: ShieldCheck, tone: "cyan", t: "FAA-verified", d: "Live registry cross-check on your cert + medical." },
                { icon: Radio,       tone: "avail", t: "Chasing-border scan animation", d: "The card lights up green + spins the PJP coin the instant anyone views it." },
                { icon: Wallet,      tone: "orange", t: "Wallet-ready", d: "Add to Apple / Google Wallet. Show anywhere without WiFi." },
                { icon: Check,       tone: "cyan", t: "Linkable from your profile", d: "One URL, one QR code. Send it to any interviewer." },
              ].map((f) => (
                <li key={f.t} className="flex gap-3">
                  <f.icon className={`h-5 w-5 mt-0.5 ${f.tone === "cyan" ? "text-cyan" : f.tone === "avail" ? "text-avail" : "text-orange-hud"}`} />
                  <div>
                    <div className="font-semibold text-white text-sm">{f.t}</div>
                    <div className="text-xs text-white/55 mt-0.5">{f.d}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="p-4 rounded-xl border border-cyan/25 bg-cyan/[0.04] mb-6">
              <div className="mono text-[10px] tracking-widest text-cyan mb-1">// ANNUAL · ${DIGITAL_ID_PRICE}/YEAR</div>
              <div className="text-sm text-white/85">
                Add to any active PJP membership. Cancels never — the badge is yours for the life of your account.
              </div>
            </div>

            <button
              onClick={buy}
              data-testid="btn-buy-digital-id"
              className="btn btn-primary text-base px-6 py-3 inline-flex w-full sm:w-auto justify-center"
            >
              {user ? `Add Digital ID · $${DIGITAL_ID_PRICE}/year` : `Sign up & get my badge`} <ArrowRight className="h-4 w-4" />
            </button>
            <div className="mono text-[10px] text-white/45 mt-3">30-day satisfaction guarantee.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function MockBadge() {
  const sampleUrl = `${window.location.origin}/id/demo-preview`;
  return (
    <div>
      <style>{`
        @keyframes pjp-chase-demo {
          0%   { background-position: 0% 50%; }
          100% { background-position: 300% 50%; }
        }
        .pjp-demo-shell {
          position: relative;
          border-radius: 20px;
          padding: 3px;
          background: linear-gradient(90deg, rgba(46,213,115,0) 0%, rgba(46,213,115,0.75) 25%, rgba(46,213,115,1) 50%, rgba(46,213,115,0.75) 75%, rgba(46,213,115,0) 100%);
          background-size: 300% 100%;
          animation: pjp-chase-demo 2.2s linear infinite;
          box-shadow: 0 0 40px rgba(46,213,115,0.35);
        }
      `}</style>
      <div className="pjp-demo-shell">
        <div className="rounded-[17px] overflow-hidden" style={{ background: "#0A0F1A" }}>
          {/* Top banner */}
          <div className="py-2.5 px-4 flex items-center justify-center gap-2 mono text-[11px] font-bold tracking-widest" style={{ background: "#2ED573", color: "#0A1F14" }}>
            <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: "#0A1F14" }} />
            PJP VERIFIED — IDENTITY CONFIRMED
            <Check className="h-3.5 w-3.5" />
          </div>

          {/* Header */}
          <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b border-white/[0.08]">
            <div style={{ width: 44, height: 44, flexShrink: 0 }}>
              <PJPCoin speed={0.32} spinning forceMotion />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-white text-[15px] tracking-tight">PRIVATE JET PILOTS</div>
              <div className="mono text-[10px] tracking-[0.24em] text-white/50 uppercase">Aviation Identity Card</div>
            </div>
          </div>

          {/* Photo + meta */}
          <div className="grid grid-cols-[132px_1fr] gap-3 p-4">
            <div className="relative aspect-[3/4] rounded-lg overflow-hidden border border-white/10 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
              <div className="text-white/70 font-display text-6xl font-bold">JC</div>
            </div>
            <div className="space-y-3">
              <MetaRow label="MEMBER ID" value="PJP-ID 00042" />
              <MetaRow label="CERTIFICATE" value="ATP 4213567" />
              <MetaRow label="FIRST CLASS" value="Exp FEB 2027" tone="orange" />
            </div>
          </div>

          {/* Name */}
          <div className="px-4 pb-3">
            <div className="font-display font-bold text-white leading-[0.95] tracking-tight" style={{ fontSize: "44px" }}>Coursey,</div>
            <div className="font-display font-bold text-white leading-[0.95] tracking-tight" style={{ fontSize: "44px" }}>Jeff</div>
            <div className="mono text-[11px] tracking-widest text-white/60 uppercase mt-2">DOB 03/14/1978</div>
            <div className="mt-3 inline-block px-3 py-1.5 rounded-md bg-white text-black mono text-[11px] font-bold tracking-widest">
              PILOT
            </div>
          </div>

          {/* Bottom row — QR + tail types marquee */}
          <div className="px-4 pb-4 pt-2 border-t border-white/[0.08]">
            <div className="flex items-end justify-between gap-3">
              <div className="flex-1 overflow-hidden">
                <div className="mono text-[9px] tracking-widest text-white/45 mb-1">TYPED AIRCRAFT</div>
                <div className="flex flex-wrap gap-1">
                  {["G550", "GLEX", "CL350", "CE750"].map((t) => (
                    <span key={t} className="mono text-[9px] px-1.5 py-0.5 rounded bg-cyan/10 text-cyan border border-cyan/30">{t}</span>
                  ))}
                </div>
              </div>
              <div className="p-1.5 rounded-md bg-white">
                <QRCodeSVG value={sampleUrl} size={56} bgColor="#ffffff" fgColor="#000000" level="M" includeMargin={false} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="text-center mt-3 mono text-[10px] text-white/45">
        LIVE PREVIEW · Your real badge uses your photo + certs
      </div>
    </div>
  );
}

function MetaRow({ label, value, tone }) {
  return (
    <div>
      <div className="mono text-[9px] tracking-widest text-white/45 uppercase">{label}</div>
      <div className={`mono text-sm ${tone === "orange" ? "text-orange-hud" : "text-white"} font-semibold`}>{value}</div>
    </div>
  );
}
